import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../../../config";
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FireIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import PropTypes from "prop-types";

// The colour scale is the whole point of this view: nothing done reads red,
// finished reads green, and everything between walks up through orange and amber.
// The classNames are written out in full because Tailwind only keeps classes it
// can literally see in the source — `bg-${colour}-500` would be stripped out.
const LEVELS = [
  {
    min: 100,
    label: "Mastered",
    bar: "bg-green-500",
    text: "text-green-700",
    chip: "bg-green-100 text-green-800",
    ring: "ring-green-500",
    soft: "bg-green-50 border-green-200",
  },
  {
    min: 70,
    label: "Strong",
    bar: "bg-lime-500",
    text: "text-lime-700",
    chip: "bg-lime-100 text-lime-800",
    ring: "ring-lime-500",
    soft: "bg-lime-50 border-lime-200",
  },
  {
    min: 40,
    label: "Halfway",
    bar: "bg-amber-400",
    text: "text-amber-600",
    chip: "bg-amber-100 text-amber-800",
    ring: "ring-amber-400",
    soft: "bg-amber-50 border-amber-200",
  },
  {
    min: 1,
    label: "Started",
    bar: "bg-orange-500",
    text: "text-orange-600",
    chip: "bg-orange-100 text-orange-800",
    ring: "ring-orange-500",
    soft: "bg-orange-50 border-orange-200",
  },
  {
    min: 0,
    label: "Not started",
    bar: "bg-red-500",
    text: "text-red-600",
    chip: "bg-red-100 text-red-700",
    ring: "ring-red-400",
    soft: "bg-red-50 border-red-200",
  },
];

const levelFor = (percent) =>
  LEVELS.find((level) => percent >= level.min) || LEVELS[LEVELS.length - 1];

const relativeTime = (value) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const REFRESH_MS = 30000;

const CourseProgress = ({ onCourseClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  // a ticker so the "updated 20s ago" line keeps moving without refetching
  const [, setTick] = useState(0);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("jstoken");
      const sessionId = localStorage.getItem("session");
      const headers = {
        ...(token
          ? {
              Authorization: token.startsWith("Bearer ")
                ? token
                : `Bearer ${token}`,
            }
          : {}),
        ...(sessionId ? { "x-session-id": sessionId } : {}),
      };
      const response = await fetch(`${config.backendUrl}/dashboard/progress`, {
        headers,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setData(payload);
      setUpdatedAt(Date.now());
      setError(null);
    } catch (err) {
      setError("Could not load your progress.");
    } finally {
      setLoading(false);
    }
  }, []);

  // first load, then keep it live: a timer plus a refresh whenever the tab is
  // looked at again, so a quiz played in another tab shows up here
  useEffect(() => {
    load();
    const timer = setInterval(() => load({ silent: true }), REFRESH_MS);
    const onFocus = () => load({ silent: true });
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  useEffect(() => {
    const ticker = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(ticker);
  }, []);

  const summary = data?.summary || {};

  const { ready, empty } = useMemo(() => {
    // derived inside the memo so its dependency is the payload itself rather than
    // a fresh array literal on every render
    const all = data?.courses || [];
    const withQuestions = all.filter((course) => course.has_questions);
    const without = all.filter((course) => !course.has_questions);
    // courses you have actually started come first, furthest along at the very
    // top, so green and amber are immediately visible and the untouched red sits
    // in a block underneath; a course-id order would hide both
    withQuestions.sort((a, b) => {
      if (b.percent !== a.percent) return b.percent - a.percent;
      return b.available - a.available;
    });
    return { ready: withQuestions, empty: without };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <div className="flex items-center gap-2 text-gray-500">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          Loading your progress...
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationTriangleIcon className="h-5 w-5" />
          {error}
        </div>
        <button
          onClick={() => load()}
          className="mt-3 text-sm font-medium text-orange-600 hover:text-orange-700"
        >
          Try again
        </button>
      </div>
    );
  }

  const overall = levelFor(summary.overall_percent || 0);
  const updatedLabel = updatedAt
    ? relativeTime(new Date(updatedAt).toISOString())
    : null;

  return (
    <div className="mb-8 space-y-4">
      {/* Overall standing: the one number that summarises the colour */}
      <div className="rounded-lg bg-white p-5 shadow-md sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="text-left">
            <div className="flex items-center gap-2">
              <AcademicCapIcon className="h-6 w-6 text-orange-500" />
              <h2 className="text-lg font-bold text-gray-900 sm:text-xl">
                Course Progress
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              How much of each course&rsquo;s question bank you have answered
              correctly. Red is untouched, green is finished.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              Live{updatedLabel ? ` · ${updatedLabel}` : ""}
            </span>
            <button
              onClick={() => load({ silent: true })}
              title="Refresh now"
              className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* the headline metric */}
        <div className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-4">
          <div className="text-left">
            <div className={`text-4xl font-bold ${overall.text}`}>
              {summary.overall_percent || 0}%
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              of all questions mastered
            </div>
          </div>
          <div className="text-left">
            <div className="text-2xl font-semibold text-gray-800">
              {summary.questions_mastered || 0}
              <span className="text-base font-normal text-gray-400">
                /{summary.questions_available || 0}
              </span>
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              questions answered correctly
            </div>
          </div>
          <div className="text-left">
            <div className="text-2xl font-semibold text-gray-800">
              {summary.overall_accuracy || 0}%
            </div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              accuracy over {summary.answers_given || 0} answers
            </div>
          </div>
        </div>

        <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className={`h-3 rounded-full ${overall.bar} transition-all duration-700`}
            style={{ width: `${summary.overall_percent || 0}%` }}
          />
        </div>

        {/* the red-to-green tally: labelled so the three boxes read as the scale
            rather than as three unrelated numbers */}
        <div className="mt-5">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Your courses by status
          </div>
          <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-left">
            <div className="text-xl font-bold text-green-700">
              {summary.completed || 0}
            </div>
            <div className="text-[11px] leading-tight text-green-800">
              Mastered
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left">
            <div className="text-xl font-bold text-amber-600">
              {summary.in_progress || 0}
            </div>
            <div className="text-[11px] leading-tight text-amber-800">
              In progress
            </div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-left">
            <div className="text-xl font-bold text-red-600">
              {summary.not_started || 0}
            </div>
            <div className="text-[11px] leading-tight text-red-800">
              Not started
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Per-course colour grid */}
      <div className="rounded-lg bg-white p-5 shadow-md sm:p-6">
        <h3 className="mb-1 text-base font-semibold text-gray-900">
          Your courses
        </h3>
        <p className="mb-4 text-sm text-gray-500">
          Tap a course to see the questions behind its score.
        </p>

        {ready.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
            No courses have questions linked yet, so there is nothing to measure.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ready.map((course) => {
              const level = levelFor(course.percent);
              const lastSeen = relativeTime(course.last_activity);
              return (
                <button
                  key={course.course_id}
                  onClick={() => onCourseClick && onCourseClick(course)}
                  aria-label={`View details for ${course.course_title}`}
                  className={`group rounded-lg border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 ${level.soft} ${level.ring}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {course.course_title}
                      </div>
                      {course.course_item && (
                        <div className="mt-0.5 font-mono text-[11px] text-gray-500">
                          {course.course_item}
                        </div>
                      )}
                    </div>
                    <div className={`text-right text-xl font-bold ${level.text}`}>
                      {course.percent}%
                    </div>
                  </div>

                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white">
                    <div
                      className={`h-2 rounded-full ${level.bar} transition-all duration-700`}
                      style={{
                        width: `${
                          course.percent > 0 ? Math.max(course.percent, 3) : 0
                        }%`,
                      }}
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${level.chip}`}
                    >
                      {level.label}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {course.mastered}/{course.available} mastered
                    </span>
                  </div>

                  <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
                    {course.accuracy !== null ? (
                      <span className="inline-flex items-center gap-1">
                        <FireIcon className="h-3.5 w-3.5 text-gray-400" />
                        {course.accuracy}% accuracy
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-400">
                        <SparklesIcon className="h-3.5 w-3.5" />
                        no answers yet
                      </span>
                    )}
                    {course.attempts > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-gray-400" />
                        {course.attempts}{" "}
                        {course.attempts === 1 ? "quiz" : "quizzes"}
                      </span>
                    )}
                    {lastSeen && <span className="ml-auto">{lastSeen}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {empty.length > 0 && (
          <div className="mt-6">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              No questions linked yet ({empty.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {empty.map((course) => (
                <span
                  key={course.course_id}
                  className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-500"
                >
                  {course.course_title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

CourseProgress.propTypes = {
  onCourseClick: PropTypes.func,
};

CourseProgress.defaultProps = {
  onCourseClick: () => {},
};

export default CourseProgress;
